import { useLazyQuery } from "@apollo/client";
import React, { useState, useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { useDebounce } from "../../../hooks/useDebounce";
import { SITES } from "../sites.queries";
import {
  SitesQuery,
  SitesQueryVariables,
} from "../../../__generated__/SitesQuery";
import { useLazySite } from "../hooks/useSite";

interface ISiteInput {
  form: UseFormReturn<any, any>;
  canSelectAddress?: boolean;
  disabled?: boolean;
  error?: string;
}

export const SiteInput: React.FC<ISiteInput> = ({
  form,
  canSelectAddress = false,
  disabled = false,
  error = null,
}) => {
  const limit = 25;
  const [isOpened, setIsOpened] = useState(false);
  const [searchFn, { data, loading, called, fetchMore }] = useLazyQuery<
    SitesQuery,
    SitesQueryVariables
  >(SITES);
  const [lazySite] = useLazySite();

  const [selectedSite, setSelectedSite] = useState<any>(null);
  const [hasBeenSelected, setSelected] = useState(false);

  const siteId = form.watch("siteId");

  useEffect(() => {
    const fetchData = async (id) => {
      const { data: siteData } = await lazySite({
        variables: { id: +id },
      });
      if (siteData?.site?.result) {
        setIsOpened(false);
        setSelected(true);
        setSearch(siteData?.site?.result?.name);
        setSelectedSite(siteData?.site?.result);
        form.setValue("siteId", id);
      }
    };

    if (siteId) {
      fetchData(siteId);
    } else {
      setSearch("");
      setSelectedSite(null);
    }
  }, [siteId]);

  const [displayedSearch, setSearch] = useState("");
  const debouncedSearchTerm = useDebounce(displayedSearch, 500);

  useEffect(
    () => {
      if (debouncedSearchTerm) {
        setIsOpened(true);
        searchFn({
          variables: {
            where: {
              search: displayedSearch,
            },
          },
        });
      }
    },
    [debouncedSearchTerm] // Only call effect if debounced search term changes
  );

  useEffect(() => {
    if (!hasBeenSelected) form.setValue("siteId", null);
  }, [hasBeenSelected]);

  return (
    <div className="flex flex-col">
      <label className="label">Cible</label>
      <input
        disabled={disabled}
        className="input"
        onChange={(e) => {
          setSearch(e.target.value);
          setSelected(false);
          form.setValue("siteId", null);
        }}
        value={displayedSearch}
      />
      {!siteId && error ? <p className="error-message mb-2">{error}</p> : null}
      {isOpened && displayedSearch !== "" && !hasBeenSelected && (
        <>
          <label className="label">Résultats</label>
          <div className="input mb-3 ">
            {loading && <h2>Chargement ...</h2>}

            {called && data?.sites?.results?.length == 0 && (
              <>
                <h2>Aucun resultat</h2>
              </>
            )}

            {data?.sites?.results?.map((site) => (
              <div
                className="mb-3 cursor-pointer"
                key={`site-${site.id}`}
                onClick={() => {
                  setIsOpened(false);
                  setSearch(site.name);
                  setSelected(true);
                  setSelectedSite(site);
                  form.setValue("siteId", site.id);
                }}
              >
                <h2>{site.name}</h2>
              </div>
            ))}

            {data?.sites.hasMore && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  fetchMore({
                    variables: {
                      where: {
                        search: displayedSearch,
                      },
                      limit,
                      offset: data?.sites?.results?.length,
                    },
                  });
                }}
                disabled={loading}
                className={`text-lg font-medium focus:outline-none text-white py-4  transition-colors ${
                  !loading
                    ? "bg-gray-700 hover:bg-gray-800"
                    : "bg-gray-300 pointer-events-none "
                }`}
              >
                {loading ? "Chargement" : "Plus de resultats"}{" "}
              </button>
            )}
          </div>
        </>
      )}

      {selectedSite && hasBeenSelected && canSelectAddress && (
        <div
          className="btn mt-2"
          onClick={() => {
            form.setValue("street", selectedSite.street);
            form.setValue("city", selectedSite.city);
            form.setValue("postal", selectedSite.postal);
            form.setValue("streetNumber", selectedSite.streetNumber);
            form.setValue("lat", selectedSite.lat);
            form.setValue("lng", selectedSite.lng);
          }}
        >
          Utiliser comme adresse
        </div>
      )}
    </div>
  );
};
